var XMLFile		 = require('../xml/XMLFile'),
	Row			 = require('./Row');

/**
 * This class represents a Sheet within an Office Open XML Spreadsheet.
 * @param {Object} oColumnConfiguration - ColumnConfiguration object that provides information about the columns
 * @constructor
 */
function Sheet(oColumnConfiguration) {
	XMLFile.call(this, Sheet.metadata);

	Object.defineProperty(this, '_columns', {value: oColumnConfiguration, writable: false});

	this._hierarchyProperty = oColumnConfiguration._hierarchyProperty;
	this._hierarchyLevel = 0;
	this._rowCount = 0;
	this._createCols();
	this._createHeader();
}

Sheet.prototype = Object.create(XMLFile.prototype);
Sheet.prototype.constructor = Sheet;

Sheet.metadata = {
	tag: 'worksheet',
	root: true,
	attributes: [
		{
			name: 'xmlns',
			value: 'http://schemas.openxmlformats.org/spreadsheetml/2006/main'
		},
		{
			name: 'xmlns',
			suffix: 'r',
			value: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
		},
		{
			name: 'xmlns',
			suffix: 'mc',
			value: 'http://schemas.openxmlformats.org/markup-compatibility/2006'
		}
	],
	properties: [
		{
			tag: 'sheetPr',
			properties: [
				{
					tag: 'outlinePr',
					attributes: [
						{
							name: 'summaryBelow',
							value: '0'
						}
					]
				}
			]
		},
		{
			id: 'dimension',
			tag: 'dimension',
			attributes: [
				{
					id: 'reference',
					name: 'ref',
					value: 'A1' // Default dimension of an empty Sheet
				}
			]
		},
		{
			id: 'views',
			tag: 'sheetViews',
			properties: [
				{
					id: 'defaultView',
					tag: 'sheetView',
					attributes: [
						{
							name: 'workbookViewId',
							value: '0'
						}
					],
					properties: [

						/* Freezes the header row */
						{
							id: 'pane',
							tag: 'pane',
							attributes: [
								{
									name: 'ySplit',
									value: '1'
								},
								{
									name: 'topLeftCell',
									value: 'A2'
								},
								{
									name: 'activePane',
									value: 'bottomLeft'
								},
								{
									name: 'state',
									value: 'frozen'
								}
							]
						}
					]
				}
			]
		},
		{
			id: 'format',
			tag: 'sheetFormatPr',
			attributes: [
				{
					name: 'defaultRowHeight',
					value: '15'
				}
			]
		},
		{
			id: 'columns',
			tag: 'cols'
		},
		{
			id: 'data',
			tag: 'sheetData'
		},
		{
			id: 'filter',
			tag: 'autoFilter',
			attributes: [
				{
					id: 'reference',
					name: 'ref',
					value: 'A1' // Default dimension of an empty Sheet
				}
			]
		},
		{
			tag: 'pageMargins',
			attributes: [
				{
					name: 'left',
					value: '0.75'
				},
				{
					name: 'right',
					value: '0.75'
				},
				{
					name: 'top',
					value: '1'
				},
				{
					name: 'bottom',
					value: '1'
				},
				{
					name: 'header',
					value: '0.5'
				},
				{
					name: 'footer',
					value: '0.5'
				}
			]
		}
	]
};

Sheet.MAX_ROWS = 1048576;

/**
 * Creates the column specific information within the worksheet.
 * Column specific information is i.E. the width or the use of the
 * bestFit functionality.
 *
 * @private
 */
Sheet.prototype._createCols = function() {
	var col, i, minWidth, width,
		autoFitButtonOffset = 2.4;

	/**
	 * Inner function for calculating the column width. The function
	 * will ceil the width to the next step of 0.25 so that the
	 * result mod step is 0.
	 * @param {number} nWidth - The width that needs to be adjusted
	 * @returns {number}  A width rounded to the accuracy of 0.25
	 */
	function roundWidth(nWidth) {
		var factor, step;

		step = 0.25;
		factor = 1 / step;

		return Math.ceil(nWidth * factor) / factor;
	}

	for (i = 0; i < this._columns.size(); i++) {
		col = this._columns.columns[i];
		minWidth = this._calculateColumnWidth(col.label.length) + autoFitButtonOffset;
		width = roundWidth(Math.max(this._calculateColumnWidth(col.width), minWidth));

		this.columns.addChild({
			tag: 'col',
			attributes: [
				{
					name: 'min',
					value: (i + 1)
				},
				{
					name: 'max',
					value: (i + 1)
				},
				{
					name: 'bestFit',
					value: '1'
				},
				{
					name: 'width',
					value: Math.max(width, minWidth)
				},
				{
					name: 'customWidth',
					value: '1'
				}
			]
		});
	}
};

/**
 * This method calculates the optimal column width for the given
 * amount of characters. It removes the dependency on font size or
 * pixel width.
 *
 * @param {number} nCharacters - Amount of characters that should fit into the column
 * @return {number} Excel internal width value corresponding to the used font size
 * @private
 */
Sheet.prototype._calculateColumnWidth = function(nCharacters) {
	var defaultFontSize = 11, // Needs to be adjusted once we support different font sizes
		defaultPaddingAndBorder = 5,
		scaleFactor = 7.33;

	if (typeof nCharacters !== 'number') {
		return 0;
	}

	return (((nCharacters * 2 / 3) * defaultFontSize) + defaultPaddingAndBorder) / scaleFactor;
};

/**
 * Sets the maximal hierarchy depth of this Sheet. The value will
 * only be changed if the given depth is higher than the previous
 * depth.
 *
 * @param {number} nHierarchyDepth - Numerical hierarchy depth
 * @private
 */
Sheet.prototype._setHierarchyDepth = function(nHierarchyDepth) {

	/* The maximum supported hierarchy depth is 7 */
	if (typeof nHierarchyDepth !== 'number' || nHierarchyDepth < 1 || nHierarchyDepth > 7) {
		return;
	}

	if (!this.format.hierarchyDepth) {
		this.format.addAttribute({
			id: 'hierarchyDepth',
			name: 'outlineLevelRow',
			value: nHierarchyDepth || 0
		});
	}

	this.format.hierarchyDepth.value = Math.max(this.format.hierarchyDepth.value, nHierarchyDepth);
};

/**
 * Requests the header Row from the ColumnConfiguration and adds it to the
 * sheet.
 *
 * @private
 */
Sheet.prototype._createHeader = function() {
	var headerRow = this._columns.getHeader();

	if (headerRow === null) {
		this.remove('filter');
		this.views.defaultView.remove('pane');

		return;
	}

	this._appendRow(headerRow);
};

/**
 * Adds the specified Row to the sheetData element of this Sheet and
 * automatically propagates its row index to the row and its cells.
 *
 * @param {Object} oRow - The Row object that should be appended to the current Sheet
 * @private
 */
Sheet.prototype._appendRow = function(oRow) {
	var dimension, sSerializedRow;

	this._rowCount++;
	oRow.setReference('' + (this._rowCount));

	sSerializedRow = oRow.serialize();

	/* Bundle two rows into one string */
	if (this._rowCount % 2 === 0) {
		sSerializedRow = this.data.childs.pop() + sSerializedRow;
	}

	/*
	 * Serializing the rows would create concatenated strings that are represented by a binary
	 * tree that references all substrings as leaves. This increases the memory usage massively.
	 *
	 * To avoid this string representation we need to convert the concatenated string to a
	 * sequential string by using String#split and Array#join. This reduces memory consumption
	 * and due to less Garbage Collection runs, also the runtime, although there is additional
	 * effort to convert the strings.
	 *
	 * Additional information can be found here:
	 * https://bugs.chromium.org/p/v8/issues/detail?id=2869
	 */
	this.data.childs.push((' ' + sSerializedRow).substr(1));

	/* Update sheet dimension */
	dimension = 'A1:' + Row.getColumnReferenceByIndex(this._columns.size() - 1) + this._rowCount;

	this.dimension.reference.value = dimension;

	if (this.filter) {
		this.filter.reference.value = dimension;
	}


};

/**
 * Creates a new group inside the sheet with the given hierarchy
 * level. All subsequent data rows will be assigned to this group.
 *
 * @function createGroup
 * @param {string} sName - Name of the group that will be displayed in the group header
 * @param {number} nLevel - Hierarchy level that will be used for outlining
 */
Sheet.prototype.createGroup = function(sName, nLevel) {
	var groupHeaderRow;

	this._hierarchyLevel = nLevel;
	groupHeaderRow = this._columns.getGroupHeader(sName);

	this._appendRow(groupHeaderRow);
};

/**
 * Requests a row from the ColumnConfiguration containing the given data
 * and adds it to the sheetData element of this Sheet.
 *
 * The maximum row limit is 1,048,576 but the user rather runs out of
 * memory than reaching this limit.
 *
 * @function insert
 * @param {Object} oData - Data object to be inserted
 */
Sheet.prototype.insert = function(oData) {
	var nHierarchyLevel;

	if (this._rowCount >= Sheet.MAX_ROWS) {
		throw new Error('Row limit reached.');
	}

	nHierarchyLevel =
		this._columns.hierarchyLevelProperty ? oData[this._columns.hierarchyLevelProperty] : this._hierarchyLevel;

	/* Create row and apply binding */
	this._appendRow(this._columns.bind(oData, nHierarchyLevel));
	this._setHierarchyDepth(nHierarchyLevel);
};

/**
 * Cleanup hook that gets executed when the XMLFil#serialize function
 * is called with the finalize flag.
 *
 * @private
 */
Sheet.prototype.cleanup = function() {
	this.remove('data');
};

/**
 * Returns the current row count of the Worksheet.
 *
 * @returns {number} - The amount of rows that are contained in this Worksheet
 *
 * @public
 */
Sheet.prototype.getRowCount = function() {
	return this._rowCount;
};

module.exports = Sheet;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/data/Sheet.js
// module id = 9
// module chunks = 0